import { parsePhoneNumber } from 'libphonenumber-js/min'

export function formatInternationalPhone(phone: string | null | undefined): string {
    if (!phone) return ''

    const clean = phone.replace(/[^\d+]/g, '')
    const withPlus = clean.startsWith('+') ? clean : `+${clean}`

    try {
        const parsed = parsePhoneNumber(withPlus)
        if (parsed) return parsed.formatInternational()
    } catch {
        // ignore parse errors, fall back to mask-based formatter
    }

    return formatPhoneNumber(phone)
}

/**
 * Форматирует номер телефона согласно маскам, используемым в ValidationInput
 * Использует ту же логику, что и компонент ValidationInput
 * @param phone - Номер телефона в формате +XXXXXXXXXXXX или XXXXXXXXXXXX
 * @returns Отформатированный номер телефона
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
    if (!phone) {
        return ''
    }

    // Очищаем номер от всех символов кроме цифр и +
    let cleanPhone = phone.replace(/[^\d+]/g, '').replace('+8', '+7')
    
    // Если номер не начинается с +, добавляем его
    if (!cleanPhone.startsWith('+')) {
        cleanPhone = '+' + cleanPhone
    }
    
    // Извлекаем только цифры для форматирования
    const digits = cleanPhone.replace(/[^\d]/g, '')
    
    // Определяем формат на основе кода страны
    if (cleanPhone.startsWith('+998') || cleanPhone.startsWith('+971')) {
        // +998 или +971: +###-##-#*
        return formatPhoneDigits(digits, '+###-##-#*')
    } else if (cleanPhone.startsWith('+996')) {
        // +996: +### ### #*
        return formatPhoneDigits(digits, '+### ### #*')
    } else if (cleanPhone.startsWith('+995')) {
        // +995: +### (###) ###-#*
        return formatPhoneDigits(digits, '+### (###) ###-#*')
    } else if (cleanPhone.startsWith('+994')) {
        // +994: +### ### ### ## #*
        return formatPhoneDigits(digits, '+### ### ### ## #*')
    } else if (cleanPhone.startsWith('+993')) {
        // +993: +###-#-###-#*
        return formatPhoneDigits(digits, '+###-#-###-#*')
    } else if (cleanPhone.startsWith('+992')) {
        // +992: +### ## ### ## #*
        return formatPhoneDigits(digits, '+### ## ### ## #*')
    } else if (cleanPhone.startsWith('+380')) {
        // +380: +### ## #*
        return formatPhoneDigits(digits, '+### ## #*')
    } else if (cleanPhone.startsWith('+375')) {
        // +375: +### ## ###-##-#*
        return formatPhoneDigits(digits, '+### ## ###-##-#*')
    } else if (cleanPhone.startsWith('+374')) {
        // +374: +###-##-###-#*
        return formatPhoneDigits(digits, '+###-##-###-#*')
    } else if (cleanPhone.startsWith('+373')) {
        // +373: +###-####-#*
        return formatPhoneDigits(digits, '+###-####-#*')
    } else if (cleanPhone.startsWith('+66')) {
        // +66: +##-##-###-##-#*
        return formatPhoneDigits(digits, '+##-##-###-##-#*')
    } else if (cleanPhone.startsWith('+62')) {
        // +62: +## ## ### ### #*
        return formatPhoneDigits(digits, '+## ## ### ### #*')
    } else if (cleanPhone.startsWith('+44')) {
        // +44: +## #### #*
        return formatPhoneDigits(digits, '+## #### #*')
    } else if (cleanPhone.startsWith('+7') || cleanPhone.startsWith('+8')) {
        // +7 или +8: +# (###) ###-##-#*
        return formatPhoneDigits(digits, '+# (###) ###-##-#*')
    } else if (cleanPhone.startsWith('+1')) {
        // +1: +# (###) ###-#*
        return formatPhoneDigits(digits, '+# (###) ###-#*')
    } else {
        // Для остальных стран используем базовую маску
        return formatPhoneDigits(digits, '+#*')
    }
}

/**
 * Применяет маску к цифрам номера телефона
 * @param digits - Только цифры номера (без +)
 * @param mask - Маска в формате v-maska (например, '+# (###) ###-##-#*')
 * @returns Отформатированный номер
 */
function formatPhoneDigits(digits: string, mask: string): string {
    if (!digits) {
        return ''
    }
    
    let result = ''
    let digitIndex = 0
    
    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
        const maskChar = mask[i]
        
        if (maskChar === '#') {
            // Заменяем # на цифру
            result += digits[digitIndex]
            digitIndex++
        } else if (maskChar === '*') {
            // * означает любое количество оставшихся цифр
            // Добавляем оставшиеся цифры группами по 2-3, разделяя дефисами или пробелами
            const remainingDigits = digits.substring(digitIndex)
            if (remainingDigits.length > 0) {
                // Определяем разделитель на основе маски после *
                const remainingMask = mask.substring(i + 1)
                let separator = ' '
                
                // Ищем разделитель в оставшейся части маски
                if (remainingMask.includes('-')) {
                    separator = '-'
                } else if (remainingMask.includes(' ')) {
                    separator = ' '
                }
                
                // Форматируем оставшиеся цифры
                if (remainingDigits.length <= 2) {
                    result += remainingDigits
                } else {
                    // Группируем по 2-3 цифры
                    let formatted = ''
                    for (let j = 0; j < remainingDigits.length; j += 2) {
                        if (j > 0) {
                            formatted += separator
                        }
                        formatted += remainingDigits.substring(j, Math.min(j + 2, remainingDigits.length))
                    }
                    result += formatted
                }
            }
            break
        } else {
            // Обычный символ маски (пробел, скобка, дефис и т.д.)
            result += maskChar
        }
    }
    
    return result
}
